import { useActionData, useLocation, useNavigation } from "@remix-run/react";
import { usePrismicLabels } from "~/hooks/usePrismicLabel";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useErrorAlert } from "@commercekit/react";
import type { HTMLComponent } from "@commercekit/react";
import type { ActionData } from "@commercekit/remix";
import {
  AuthActions,
  loginValidator,
  useLocalePath,
  useLogin,
  useRequestActivationEmail,
} from "@commercekit/remix";
import { ValidatedForm } from "remix-validated-form";
import { useEnv } from "~/store/globalState";
import WvFormHiddenInputs from "../atoms/WvFormHiddenInputs";
import WvField from "../molecules/WvField";
import WvInputField from "../molecules/WvInputField";
import WvLink from "../atoms/WvLink";
import WvPageLoader from "../molecules/WvPageLoader";
import { useWvLogin } from "~/hooks/useDataLayer";
import { setUserEmailCookie } from "~/hooks/useUserEmailCookie";

const WvLoginForm: HTMLComponent<{ inActiveAccountMessage?: string }> = ({
  inActiveAccountMessage = "",
}) => {
  const { $pt } = usePrismicLabels();
  const { search } = useLocation();
  const localePath = useLocalePath();
  const gtmLogin = useWvLogin();
  const { formAction } = useNavigation();
  const [showError, setShowError] = useState(false);
  const [isInactive, setIsInactive] = useState(false);
  const [hasShownRequestSuccess, setHasShownRequestSuccess] = useState(false);
  const [accountEmail, setAccountEmail] = useState("");
  const { show } = useErrorAlert();
  const { requestActivationEmail, requestError, requestingEmail, requestSent } =
    useRequestActivationEmail();

  const loading = useMemo(
    () => formAction?.split("?")[0] === localePath("/customer/login"),
    [localePath, formAction]
  );

  const actionData = useActionData<ActionData<AuthActions>>();
  const inActiveErrorCode = "account_inactive";

  const InActiveErrorElement: HTMLComponent = () => (
    <div
      className="alert alert-danger"
      dangerouslySetInnerHTML={{
        __html: inActiveAccountMessage,
      }}
    ></div>
  );

  const requestAccountActivation = useCallback(() => {
    requestActivationEmail(accountEmail);
  }, [accountEmail, requestActivationEmail]);

  useEffect(() => {
    const activationLink = document.querySelector(
      ".alert-danger #activation-link"
    );
    activationLink?.addEventListener("click", requestAccountActivation);
    return () =>
      activationLink?.removeEventListener("click", requestAccountActivation);
  });

  useEffect(() => {
    if (
      actionData?.action === AuthActions.LOGIN &&
      actionData?.errors &&
      actionData.errors.length &&
      !loading
    ) {
      setShowError(true);
      setIsInactive(
        actionData.errors?.some(
          (item) => item?.split("__")?.[1] === inActiveErrorCode
        )
      );
    }
    if (loading) setShowError(false);
  }, [$pt, actionData, loading, show]);

  useEffect(() => {
    if (requestingEmail) setHasShownRequestSuccess(false);
    if (requestError?.length && !requestingEmail)
      show(requestError?.map((e) => $pt(e)).join("; "), "danger");
  }, [show, requestError, requestingEmail, $pt]);

  useEffect(() => {
    if (requestSent && !hasShownRequestSuccess) {
      show($pt("account_activation_email_sent"), "success");
      setHasShownRequestSuccess(true);
    }
  }, [show, requestSent, hasShownRequestSuccess, $pt]);

  const redirect = useMemo(
    () =>
      new URLSearchParams(search).get("redirect") || localePath("/customer"),
    [localePath, search]
  );
  useLogin();

  const passwordValidator = useEnv();

  return (
    <div className="customer__form form content">
      <WvPageLoader
        loading={requestingEmail}
        loadingText={$pt("account_activation_email_loading")}
      />

      {showError ? (
        isInactive ? (
          <InActiveErrorElement />
        ) : (
          <div className="alert alert-danger">
            {$pt("login_message_invalid_email_or_password")}
          </div>
        )
      ) : null}
      <ValidatedForm
        validator={loginValidator(passwordValidator)}
        method="post"
        reloadDocument
      >
        <WvFormHiddenInputs
          data={{
            _action: AuthActions.LOGIN,
            redirect,
          }}
        />
        <div className="row">
          <WvField
            name="email"
            label={$pt("form_label_email")}
            required
            fieldClass="col-12"
          >
            <WvInputField
              initialValue=""
              name="email"
              placeholder={$pt("form_placeholder_email")}
              type="email"
              autoComplete="username"
              onChange={(email) => setAccountEmail(email)}
            />
          </WvField>
        </div>

        <div className="row">
          <WvField
            name="password"
            label={$pt("form_label_password")}
            required
            fieldClass="col-12"
          >
            <WvInputField
              initialValue=""
              name="password"
              placeholder={$pt("form_placeholder_password")}
              type="password"
              autoComplete="current-password"
            />
          </WvField>
        </div>

        <div className="mt-3 mb-3 row">
          <div className="col-12">
            <button
              type="submit"
              name="button"
              onClick={() => {
                gtmLogin(accountEmail);
                if (accountEmail) setUserEmailCookie(accountEmail);
              }}
              className="mr-3 btn btn-primary"
            >
              {loading ? (
                <>
                  <i data-icon-before="" className="mr-3 zmdi-hc-spin"></i>{" "}
                  {$pt("register_label_submitting")}
                </>
              ) : (
                <>{$pt("login_action_submit")}</>
              )}
            </button>
            <WvLink to={localePath("/customer/password/reset/")}>
              {$pt("login_action_go_to_forget_password_page")}{" "}
            </WvLink>
          </div>
        </div>

        <div className="mt-4 mb-3 row">
          <div className="col-12">
            {$pt("login_label_no_account")}?{" "}
            <WvLink to={localePath("/customer/register")}>
              {$pt("login_action_go_to_register_page")}
            </WvLink>{" "}
            .
          </div>
        </div>
      </ValidatedForm>
    </div>
  );
};
export default WvLoginForm;
